/**
 * App-specific role definitions
 * 
 * Each app can define its own set of roles. When configuring
 * default member roles or assigning user-specific roles, use
 * these definitions to ensure consistency.
 */

export interface AppRoleOption {
  value: string;
  label: string;
}

export const APP_ROLES: Record<string, AppRoleOption[]> = {
  playbook: [
    { value: 'admin', label: 'Admin' },
    { value: 'author', label: 'Author' },
    { value: 'contributor', label: 'Contributor' },
    { value: 'viewer', label: 'Viewer' },
  ],
  helpdesk: [
    { value: 'admin', label: 'Admin' },
    { value: 'agent', label: 'Agent' },
    { value: 'viewer', label: 'Viewer' },
  ],
  forms: [
    { value: 'admin', label: 'Admin' },
    { value: 'editor', label: 'Editor' },
    { value: 'viewer', label: 'Viewer' },
  ],
  // Default roles for apps without specific configuration
  default: [
    { value: 'admin', label: 'Admin' },
    { value: 'editor', label: 'Editor' },
    { value: 'viewer', label: 'Viewer' },
  ],
};

/**
 * Default member role for each app when added to a workspace.
 * If not specified, no default role is set (null).
 */
export const APP_DEFAULT_MEMBER_ROLES: Record<string, string> = {
  playbook: 'author',
  helpdesk: 'agent',
  forms: 'editor',
};

/**
 * Get role options for a specific app
 * @param appSlug - The app's slug (e.g., 'playbook', 'forms')
 * @returns Array of role options for the app
 */
export function getAppRoles(appSlug: string | undefined): AppRoleOption[] {
  return appSlug ? APP_ROLES[appSlug] || APP_ROLES.default : APP_ROLES.default;
}

/**
 * Get the default member role for an app
 * @param appSlug - The app's slug (e.g., 'playbook', 'forms')
 * @returns The default role for the app, or null if not configured
 */
export function getAppDefaultMemberRole(appSlug: string | undefined): string | null {
  return appSlug ? APP_DEFAULT_MEMBER_ROLES[appSlug] || null : null;
}

/**
 * Get role options for a specific app, with an "inherit" option prepended
 * Useful for dropdowns where clearing the role means inheriting from workspace
 * @param appSlug - The app's slug (e.g., 'playbook', 'forms')
 * @param inheritLabel - Label for the inherit option (default: 'Inherit from workspace')
 * @returns Array of role options with inherit option first
 */
export function getAppRoleOptionsWithInherit(
  appSlug: string | undefined,
  inheritLabel = 'Choose role'
): AppRoleOption[] {
  return [{ value: '', label: inheritLabel }, ...getAppRoles(appSlug)];
}
